import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Head from 'next/head'
import Hidden from '@material-ui/core/Hidden'
import Clipboard from 'react-clipboard.js'
import withStyles from 'components/styles/withStyles'
import Svg from 'components/Svg'
import Link from 'components/Link'
import ExecutiveSummary from 'components/ExecutiveSummary'
import TopSources from 'components/TopSources'
import ContactCard from 'components/ContactCard'

export const styles = theme => ({
  root: {
    maxWidth: 1440,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: `${theme.utils.spacing(2)}  20px ${theme.utils.spacing(7)} 20px`,
    [theme.breakpoints.up('md')]: {
      alignItems: 'flex-start',
      flexDirection: 'row',
      padding: `${theme.utils.spacing(5)} ${theme.utils.spacing(
        18,
      )} ${theme.utils.spacing(18)} ${theme.utils.spacing(12)}`,
    },
  },
  sidebar: {
    width: '100%',
    '& > * > h1': {
      ...theme.typography.h1,
      margin: 0,
      marginBottom: theme.utils.spacing(5),
    },
    '& > * + *': {
      marginTop: `${theme.spacing.unit * 2}px`,
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: 420,
      marginRight: theme.utils.spacing(11),
    },
  },
  articleBar: {
    width: '100%',
    maxWidth: 700,
    marginTop: `${theme.spacing.unit * 3}px`,

    '& h1': {
      ...theme.typography.h1,
      margin: 0,
      marginBottom: theme.utils.spacing(5),
    },

    [theme.breakpoints.up('md')]: {
      marginTop: 0,
    },
  },
  content: {
    '& > h1': {
      ...theme.typography.h1,
      margin: 0,
      marginBottom: theme.utils.spacing(5),
    },
    '& > h2': {
      ...theme.typography.articleH2,
      margin: 0,
      marginBottom: theme.utils.spacing(3),
    },
    '& > h3': {
      ...theme.typography.h3,
    },
    '& > p, & > ul li': {
      ...theme.typography.body1,
    },
    '& > p': {
      marginBottom: theme.utils.spacing(5),
    },
    '& > ul': {
      listStyle: 'none',
      marginBottom: theme.utils.spacing(5),
      marginTop: theme.utils.spacing(4),
      '& li': {
        position: 'relative',
        '& + li': {
          marginTop: theme.utils.spacing(4),
        },
        '&::before': {
          position: 'absolute',
          content: '""',
          height: 25,
          width: 25,
          top: 2,
          bottom: 0,
          left: theme.utils.spacing(-5),
          right: 0,
          backgroundImage: `url('data:image/svg+xml;utf8,<svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" fill="%23A385FB" viewBox="0 0 24 24"><path d="M10 17c-.26 0-.51-.1-.71-.29l-4-4A.996.996 0 1 1 6.7 11.3l3.3 3.29 7.29-7.29a.996.996 0 1 1 1.41 1.41l-8 8c-.19.19-.44.29-.7.29z"/><path d="M12 24C5.38 24 0 18.62 0 12S5.38 0 12 0s12 5.38 12 12-5.38 12-12 12zm0-22C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2z"/></svg>')`,
        },
      },
    },

    '& > ol': {
      listStyle: 'none',
      marginBottom: theme.utils.spacing(5),
      ...theme.typography.body1,
      '& li': {
        counterIncrement: 'li',
        position: 'relative',
        '& + li': {
          marginTop: theme.utils.spacing(4),
        },
      },
      '& li::before': {
        content: 'counter(li) "."',
        position: 'absolute',
        left: -35,
        color: theme.palette.primary.main,
        fontWeight: theme.typography.fontWeightMedium,
      },
    },

    '& .wp-block-image': {
      position: 'relative',
      margin: `${theme.utils.spacing(7)} 0`,
      '& img': {
        width: '100%',
        height: '100%',
      },
    },
    '& .wp-block-image figcaption': {
      ...theme.typography.body2,
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 70,
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.utils.spacing(4),
      background: 'rgba(255, 255, 255, 0.8)',
      textTransform: 'uppercase',
      fontWeight: theme.typography.fontWeightRegular,
    },
    '& .wp-block-embed-youtube': {
      margin: 0,
    },

    '& .wp-block-embed__wrapper': {
      position: 'relative',
      paddingBottom: '56.25%',
      paddingTop: 30,
      height: 0,
      overflow: 'hidden',
      margin: '56px 0',
      '& iframe': {
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
      },
    },
  },
  iconBar: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: `${theme.spacing.unit * 4}px`,
    '@media (max-width: 360px)': {
      flexDirection: 'column',
      alignItems: 'center',
    },
    [theme.breakpoints.up('md')]: {
      justifyContent: 'flex-start',
    },
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    cursor: 'pointer',
    ...theme.typography.h3,
    textDecoration: 'none',
    transition: 'all 0.2s linear',
    '& svg': {
      fill: theme.palette.primary.main,
      marginRight: 20,
    },
    '& path': {
      transition: 'all 0.2s linear',
    },
    '&:first-child': {
      marginRight: 40,
      '@media (max-width: 360px)': {
        marginRight: 0,
      },
    },
    '&:hover': {
      color: theme.palette.accent.purple,
      '& svg': {
        fill: theme.palette.accent.purple,
      },
    },
  },
  contactCard: {
    '& > h3': {
      ...theme.typography.h3,
      marginBottom: theme.utils.spacing(3),
    },
  },
})

const Article = ({
  classes,
  page,
  content,
  sources,
  pdf,
  link,
  executiveSummary,
  contactCard,
}) => {
  const [copied, setCopied] = useState(false)
  const handleClipboardSuccess = () => {
    setCopied(true)

    setTimeout(() => setCopied(false), 2000)
  }

  const showSidebar = Boolean(
    (executiveSummary && executiveSummary.title && executiveSummary.summary) ||
      sources.length > 0,
  )

  return (
    <div className={classes.root}>
      <Head>
        <title>{page && page.title}</title>
      </Head>

      {showSidebar && (
        <div className={classes.sidebar}>
          <Hidden mdUp implementation="css">
            <h1>{page.title}</h1>
          </Hidden>

          {executiveSummary &&
            executiveSummary.title &&
            executiveSummary.summary && (
              <ExecutiveSummary {...executiveSummary} />
            )}

          {sources.length > 0 && (
            <TopSources>
              <ol>
                {sources.map((source, i) => (
                  <li key={`source-${source.url}-${i}`}>
                    <Link href={source.url}>{source.title}</Link>
                  </li>
                ))}
              </ol>
            </TopSources>
          )}
        </div>
      )}

      <div className={classes.articleBar}>
        <Hidden smDown implementation="css">
          <h1>{page.title}</h1>
        </Hidden>

        <div className={classes.iconBar}>
          {pdf && (
            <a
              href={pdf}
              className={classes.icon}
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              <Svg.Download width="20" />
              <span>Get PDF</span>
            </a>
          )}

          <Clipboard
            className={classes.icon}
            data-clipboard-text={link}
            onSuccess={handleClipboardSuccess}
          >
            {copied === false ? (
              <>
                <Svg.Copy width="24" />
                <span>Copy link</span>
              </>
            ) : (
              <>
                <Svg.CopyCheck width="24" />
                <span>Copied</span>
              </>
            )}
          </Clipboard>
        </div>

        <div
          className={classes.content}
          dangerouslySetInnerHTML={{ __html: content }}
        />

        {contactCard.name && contactCard.description && (
          <div className={classes.contactCard}>
            <h3>Author</h3>
            <ContactCard {...contactCard} />
          </div>
        )}
      </div>
    </div>
  )
}

Article.propTypes = {
  classes: PropTypes.object.isRequired,
  page: PropTypes.object.isRequired,
  content: PropTypes.string.isRequired,
  pdf: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  sources: PropTypes.array,
  executiveSummary: PropTypes.object.isRequired,
  contactCard: PropTypes.object.isRequired,
}

export default withStyles(styles)(Article)
